// Naming and handoff rules for shared element page transitions. Pure data, no
// DOM, so the tests can pin the names and the controller in
// src/lib/viewTransitions.ts stays the only code that touches styles.
//
// A source (a cover on a card, a title, an avatar) is marked with a data
// attribute that holds its key, for example data-vt-art="4aawyAB9vmqN3uQ7FjRGTy".
// Nothing carries a view-transition-name until a navigation starts. Then the
// controller names the one element that was clicked and the matching hero on
// the destination page, so no page ever holds the same name twice.

export type SharedKind = "art" | "title" | "avatar" | "stage" | "video";

export const SHARED_KINDS: readonly SharedKind[] = ["art", "title", "avatar", "stage", "video"];

/** Marks the destination element for a kind, e.g. the release page's main artwork. */
export const HERO_ATTR = "data-vt-hero";

export const sharedAttr = (kind: SharedKind): string => `data-vt-${kind}`;

/** The view-transition-class every shared group gets, so one CSS rule styles them all. */
export const sharedClass = (kind: SharedKind): string => `vt-${kind}`;

const hash = (value: string): string => {
  // FNV-1a, 32 bit. Only used to keep two different keys that slug the same
  // (like "Ashes & Wind" and "Ashes Wind") apart.
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
};

/**
 * First non-empty part, trimmed. Callers pass the Spotify album id first and a
 * name after it, so a card with an id and a card without one still agree when
 * both only know the name.
 */
export function sharedKey(...parts: Array<string | number | null | undefined>): string {
  for (const part of parts) {
    if (part === null || part === undefined) continue;
    const text = String(part).trim();
    if (text) return text;
  }
  return "";
}

/**
 * A valid CSS custom ident, unique per kind and key. Spotify ids pass through
 * untouched; anything else is slugged and gets a short hash of the original.
 */
export function sharedName(kind: SharedKind, key: string): string {
  const raw = sharedKey(key);
  if (!raw) return "";
  const slug = raw.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/-{2,}/g, "-").replace(/^-|-$/g, "").slice(0, 48);
  const suffix = slug === raw ? "" : `${slug ? "-" : ""}${hash(raw)}`;
  return `vt-${kind}-${slug}${suffix}`;
}

type DataProps = Record<string, string>;

/** Props for a source element: `<img {...sharedProps("art", album.id)} />`. */
export function sharedProps(kind: SharedKind, ...keyParts: Array<string | number | null | undefined>): DataProps {
  const key = sharedKey(...keyParts);
  return key ? { [sharedAttr(kind)]: key } : {};
}

/** Props for the destination element of one or more kinds. */
export function heroProps(...kinds: SharedKind[]): DataProps {
  return kinds.length ? { [HERO_ATTR]: kinds.join(" ") } : {};
}

// Handoff: what the destination needs to paint its hero on the first commit,
// before its own data arrives. Kept in memory only, keyed by path.

export interface SharedHandoff {
  key: string;
  kinds: SharedKind[];
  /** The exact image URL the source had on screen, so it is already decoded. */
  artwork?: string;
  title?: string;
  at: number;
}

export const HANDOFF_TTL_MS = 10 * 60 * 1000;
export const HANDOFF_MAX_ENTRIES = 24;

const handoffs = new Map<string, SharedHandoff>();

export function handoffPath(path: string): string {
  let pathname = String(path || "").split(/[?#]/)[0] || "/";
  try {
    pathname = decodeURIComponent(pathname);
  } catch {
    // keep the raw path
  }
  pathname = pathname.toLowerCase().replace(/\/+$/, "");
  return pathname || "/";
}

export function writeHandoff(path: string, handoff: Omit<SharedHandoff, "at">, now = Date.now()): SharedHandoff {
  const key = handoffPath(path);
  const entry = { ...handoff, at: now };
  handoffs.delete(key);
  handoffs.set(key, entry);
  while (handoffs.size > HANDOFF_MAX_ENTRIES) {
    const oldest = handoffs.keys().next();
    if (oldest.done) break;
    handoffs.delete(oldest.value);
  }
  return entry;
}

export function readHandoff(path: string, now = Date.now()): SharedHandoff | null {
  const key = handoffPath(path);
  const entry = handoffs.get(key);
  if (!entry) return null;
  if (now - entry.at > HANDOFF_TTL_MS) {
    handoffs.delete(key);
    return null;
  }
  return entry;
}

export function clearHandoffs(): void {
  handoffs.clear();
}
